export const PROJECT_SEARCH_OPEN_TRANSITION_STORAGE_KEY = 'project-search-open-transition';
export const PROJECT_SEARCH_OPEN_TRANSITION_MAX_AGE_MS = 5000;
export const PROJECT_SEARCH_READY_GATE_MIN_MS = 320;
/** Never block the project search route longer than this — avoids infinite loading on Firestore failures. */
export const PROJECT_SEARCH_ENTRY_GATE_MAX_MS = 12000;
export const PROJECT_SEARCH_INITIAL_SECTION_PREVIEW_MS = 140;
export const PROJECT_SEARCH_SECTION_REVEAL_OVERLAP_DELAY_MS = 55;
export const PROJECT_SEARCH_SECTION_REVEAL_DURATION_MS = 180;
export const PROJECT_SEARCH_OPEN_TRACE_COMPLETE_MARK = 'transition-complete';
export const PROJECT_SEARCH_OPEN_TRACE_SKIPPED_MARK = 'transition-skipped';

export type ProjectSearchOpenTransitionSource = 'projects-list' | 'page-refresh' | 'direct-link';

export type ProjectSearchSectionKey = 'queue' | 'sources' | 'assets' | 'empty';

export type ProjectSearchOpenTraceMark =
  | 'navigation-start'
  | 'session-ready'
  | 'project-home-mounted'
  | 'queue-ready'
  | 'sources-ready'
  | 'assets-ready'
  | typeof PROJECT_SEARCH_OPEN_TRACE_COMPLETE_MARK
  | typeof PROJECT_SEARCH_OPEN_TRACE_SKIPPED_MARK;

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export interface ProjectSearchOpenTransitionEntry {
  sessionId: string;
  source: ProjectSearchOpenTransitionSource;
  startedAt: number;
}

type ReadySections = Record<ProjectSearchSectionKey, boolean>;

interface ProjectSearchOpenTraceSummary {
  sessionId: string;
  completedMark:
    | typeof PROJECT_SEARCH_OPEN_TRACE_COMPLETE_MARK
    | typeof PROJECT_SEARCH_OPEN_TRACE_SKIPPED_MARK;
  elapsedMsByMark: Partial<Record<ProjectSearchOpenTraceMark, number>>;
  totalMs: number;
}

interface ProjectSearchOpenTrace {
  startedAt: number;
  marks: Partial<Record<ProjectSearchOpenTraceMark, number>>;
}

const projectSearchOpenTraces = new Map<string, ProjectSearchOpenTrace>();
let hasConsumedProjectSearchReloadEntry = false;

// Dev HMR can reload this module without a full page reload; reset reload-only gate so tests/dev behave consistently.
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    hasConsumedProjectSearchReloadEntry = false;
  });
}

export function createProjectSearchOpenTransitionEntry({
  sessionId,
  startedAt,
  source = 'projects-list',
}: {
  sessionId: string;
  startedAt: number;
  source?: ProjectSearchOpenTransitionSource;
}): ProjectSearchOpenTransitionEntry {
  return {
    sessionId,
    source,
    startedAt,
  };
}

export function storeProjectSearchOpenTransitionEntry({
  storage,
  entry,
}: {
  storage: StorageLike;
  entry: ProjectSearchOpenTransitionEntry;
}): void {
  storage.setItem(PROJECT_SEARCH_OPEN_TRANSITION_STORAGE_KEY, JSON.stringify(entry));
}

export function consumeProjectSearchOpenTransitionEntry({
  storage,
  sessionId,
  now,
  maxAgeMs = PROJECT_SEARCH_OPEN_TRANSITION_MAX_AGE_MS,
}: {
  storage: StorageLike;
  sessionId: string;
  now: number;
  maxAgeMs?: number;
}): ProjectSearchOpenTransitionEntry | null {
  const rawValue = storage.getItem(PROJECT_SEARCH_OPEN_TRANSITION_STORAGE_KEY);

  if (!rawValue) {
    return null;
  }

  storage.removeItem(PROJECT_SEARCH_OPEN_TRANSITION_STORAGE_KEY);

  try {
    const parsedValue = JSON.parse(rawValue) as ProjectSearchOpenTransitionEntry;
    const isCurrentSession = parsedValue.sessionId === sessionId;
    const isFresh = now - parsedValue.startedAt <= maxAgeMs;

    if (!isCurrentSession || !isFresh || parsedValue.source !== 'projects-list') {
      return null;
    }

    return parsedValue;
  } catch {
    return null;
  }
}

export function getProjectSearchInitialOpenTransitionEntry({
  storage,
  sessionId,
  now,
  navigationType,
}: {
  storage: StorageLike;
  sessionId: string;
  now: number;
  navigationType?: 'navigate' | 'reload' | 'back_forward' | 'prerender';
}): ProjectSearchOpenTransitionEntry | null {
  const storedEntry = consumeProjectSearchOpenTransitionEntry({
    storage,
    sessionId,
    now,
  });

  if (storedEntry) {
    return storedEntry;
  }

  if (navigationType === 'reload' && !hasConsumedProjectSearchReloadEntry) {
    hasConsumedProjectSearchReloadEntry = true;
    return createProjectSearchOpenTransitionEntry({
      sessionId,
      startedAt: now,
      source: 'page-refresh',
    });
  }

  if (navigationType === 'navigate') {
    return createProjectSearchOpenTransitionEntry({
      sessionId,
      startedAt: now,
      source: 'direct-link',
    });
  }

  return null;
}

export function getProjectSearchRevealOrder({
  showEmptyState,
  hasQueueSection,
  hasSourcesSection,
  hasAssetsSection,
}: {
  showEmptyState: boolean;
  hasQueueSection: boolean;
  hasSourcesSection: boolean;
  hasAssetsSection: boolean;
}): ProjectSearchSectionKey[] {
  if (showEmptyState) {
    return ['empty'];
  }

  const revealOrder: ProjectSearchSectionKey[] = [];

  if (hasQueueSection) {
    revealOrder.push('queue');
  }

  if (hasSourcesSection) {
    revealOrder.push('sources');
  }

  if (hasAssetsSection) {
    revealOrder.push('assets');
  }

  return revealOrder;
}

export function shouldRunProjectSearchOpenTransition({
  hasPendingOpenTransition = false,
  pendingOpenTransitionSource = hasPendingOpenTransition ? 'projects-list' : null,
  revealOrder,
  readySections: _readySections,
}: {
  hasPendingOpenTransition?: boolean;
  pendingOpenTransitionSource?: ProjectSearchOpenTransitionSource | null;
  revealOrder: ProjectSearchSectionKey[];
  readySections: ReadySections;
}): boolean {
  if (!pendingOpenTransitionSource || revealOrder.length === 0) {
    return false;
  }

  if (
    pendingOpenTransitionSource === 'page-refresh' ||
    pendingOpenTransitionSource === 'projects-list' ||
    pendingOpenTransitionSource === 'direct-link'
  ) {
    return true;
  }

  return revealOrder.some((sectionKey) => !_readySections[sectionKey]);
}

export function getInitialProjectSearchRevealDelayMs(revealedSectionCount: number): number {
  return revealedSectionCount === 0
    ? PROJECT_SEARCH_INITIAL_SECTION_PREVIEW_MS
    : PROJECT_SEARCH_SECTION_REVEAL_OVERLAP_DELAY_MS;
}

export function getProjectSearchEntryPresentation({
  pendingOpenTransitionSource,
  hasCurrentSession = true,
  isProjectSearchReady,
  elapsedMsSinceEntryStart,
  minGateMs = PROJECT_SEARCH_READY_GATE_MIN_MS,
  maxGateMs = PROJECT_SEARCH_ENTRY_GATE_MAX_MS,
}: {
  pendingOpenTransitionSource: ProjectSearchOpenTransitionSource | null;
  hasCurrentSession?: boolean;
  isProjectSearchReady: boolean;
  elapsedMsSinceEntryStart: number;
  minGateMs?: number;
  maxGateMs?: number;
}): 'loading' | 'ready' {
  if (!pendingOpenTransitionSource) {
    return 'ready';
  }

  if (elapsedMsSinceEntryStart >= maxGateMs) {
    return 'ready';
  }

  if (!hasCurrentSession || !isProjectSearchReady) {
    return 'loading';
  }

  if (elapsedMsSinceEntryStart < minGateMs) {
    return 'loading';
  }

  return 'ready';
}

export function shouldBypassAppLoaderForProjectSearch({
  pathname,
  routeName,
}: {
  pathname?: string;
  routeName?: string;
}): boolean {
  if (routeName === 'session-search' || routeName === 'session-home') {
    return true;
  }

  if (!pathname) {
    return false;
  }

  return (
    /^\/project\/[^/]+\/search(?:\/)?$/.test(pathname) || /^\/project\/[^/]+(?:\/)?$/.test(pathname)
  );
}

export function getProjectSearchRenderedSections({
  isTransitionActive,
  revealOrder,
  revealedSections,
}: {
  isTransitionActive: boolean;
  revealOrder: ProjectSearchSectionKey[];
  revealedSections: ProjectSearchSectionKey[];
}): ProjectSearchSectionKey[] {
  if (!isTransitionActive) {
    return revealOrder;
  }

  return revealOrder.filter((sectionKey) => revealedSections.includes(sectionKey));
}

export function markProjectSearchOpenTrace({
  sessionId,
  mark,
  now,
}: {
  sessionId: string;
  mark: ProjectSearchOpenTraceMark;
  now: number;
}): ProjectSearchOpenTraceSummary | null {
  if (mark === 'navigation-start') {
    projectSearchOpenTraces.set(sessionId, {
      startedAt: now,
      marks: {
        'navigation-start': now,
      },
    });
    return null;
  }

  const existingTrace = projectSearchOpenTraces.get(sessionId);

  if (!existingTrace) {
    return null;
  }

  existingTrace.marks[mark] = now;

  if (
    mark !== PROJECT_SEARCH_OPEN_TRACE_COMPLETE_MARK &&
    mark !== PROJECT_SEARCH_OPEN_TRACE_SKIPPED_MARK
  ) {
    return null;
  }

  const elapsedMsByMark = Object.entries(existingTrace.marks).reduce<
    Partial<Record<ProjectSearchOpenTraceMark, number>>
  >((result, [traceMark, traceTime]) => {
    if (typeof traceTime === 'number') {
      result[traceMark as ProjectSearchOpenTraceMark] = traceTime - existingTrace.startedAt;
    }

    return result;
  }, {});

  projectSearchOpenTraces.delete(sessionId);

  return {
    sessionId,
    completedMark: mark,
    elapsedMsByMark,
    totalMs: now - existingTrace.startedAt,
  };
}

export function logProjectSearchOpenTraceSummary(
  summary: ProjectSearchOpenTraceSummary | null,
  log: (message: string, payload: ProjectSearchOpenTraceSummary) => void = console.info,
): void {
  if (!summary || process.env.NODE_ENV === 'production') {
    return;
  }

  log('[ProjectSearchLoading]', summary);
}

export function resetProjectSearchLoadingForTests(): void {
  projectSearchOpenTraces.clear();
  hasConsumedProjectSearchReloadEntry = false;
}
